<script setup>
import {businessContact} from '@/utils/contact'
import {computed, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import {useHead} from '@vueuse/head'
import axios from 'axios'
import HomeHeader from '@/components/HomeHeader.vue'
import Footer from '@/components/Footer.vue'
import MarketingInquiryForm from '@/components/MarketingInquiryForm.vue'
import {useI18n} from '@/i18n'
import {applyImageFallback, excerptFromHtml, itemsFrom} from '@/utils/detailContent'

const route = useRoute()
const {t} = useI18n()
const detail = ref(null)
const relatedProducts = ref([])
const selectedImageIndex = ref(0)
const loading = ref(true)
const error = ref(false)

const images = computed(() => {
  const values = [detail.value?.image_1, detail.value?.image_2, detail.value?.image_3].filter(Boolean)
  return [...new Set(values)]
})
const selectedImage = computed(() => images.value[selectedImageIndex.value] || '/images/engineering/slider_1.jpg')
const lead = computed(() => detail.value?.overview || t('marketing.detail.product.leadFallback'))
const pageContext = computed(() => `${t('page.singleProduct')}: ${detail.value?.product_title || route.params.str}`)

async function loadProduct(slug) {
  loading.value = true
  error.value = false
  selectedImageIndex.value = 0
  relatedProducts.value = []
  try {
    const detailResponse = await axios.get('/api/products/', {params: {site: slug}})
    detail.value = itemsFrom(detailResponse)[0] || null
    if (!detail.value) {
      error.value = true
      return
    }
    loading.value = false

    try {
      const relatedResponse = await axios.get('/api/products/')
      relatedProducts.value = itemsFrom(relatedResponse)
        .filter((item) => item.id !== detail.value.id)
        .slice(0, 3)
    } catch (relatedError) {
      relatedProducts.value = []
    }
  } catch (requestError) {
    error.value = true
  } finally {
    loading.value = false
  }
}

watch(() => route.params.str, (slug) => loadProduct(slug), {immediate: true})

useHead({
  title: () => detail.value?.product_title || t('page.singleProduct'),
  meta: [
    {name: 'description', content: () => detail.value?.overview || excerptFromHtml(detail.value?.Description, 160)}
  ]
})
</script>

<template>
  <div class="page mkt-page mkt-detail-page">
    <HomeHeader/>
    <main>
      <section v-if="loading" class="mkt-detail-state">
        <i class="mdi mdi-loading mdi-spin" aria-hidden="true"></i>
        <p>{{ t('marketing.detail.common.loading') }}</p>
      </section>

      <section v-else-if="error || !detail" class="mkt-detail-state is-error">
        <i class="mdi mdi-alert-circle-outline" aria-hidden="true"></i>
        <h1>{{ t('marketing.detail.common.notFound') }}</h1>
        <a class="mkt-button mkt-button-primary" href="/products">{{ t('page.products') }}</a>
      </section>

      <template v-else>
        <section class="mkt-product-detail-hero">
          <div class="mkt-shell">
            <nav class="mkt-detail-breadcrumb" aria-label="Breadcrumb">
              <a href="/">{{ t('common.home') }}</a><i class="mdi mdi-chevron-right" aria-hidden="true"></i>
              <a href="/products">{{ t('page.products') }}</a><i class="mdi mdi-chevron-right" aria-hidden="true"></i>
              <span>{{ detail.product_title }}</span>
            </nav>

            <div class="mkt-product-detail-grid">
              <div class="mkt-product-gallery">
                <div class="mkt-product-gallery-main">
                  <img :src="selectedImage" :alt="detail.product_title" @error="applyImageFallback">
                </div>
                <div v-if="images.length > 1" class="mkt-product-thumbnails" :aria-label="t('marketing.detail.common.image')">
                  <button v-for="(image, index) in images" :key="image" type="button"
                          :class="{active: selectedImageIndex === index}"
                          :aria-label="`${t('marketing.detail.common.image')} ${index + 1}`"
                          @click="selectedImageIndex = index">
                    <img :src="image" :alt="`${detail.product_title} ${index + 1}`" @error="applyImageFallback">
                  </button>
                </div>
              </div>

              <div class="mkt-product-detail-copy">
                <span class="mkt-eyebrow">{{ t('marketing.detail.product.eyebrow') }}</span>
                <h1>{{ detail.product_title }}</h1>
                <p class="mkt-detail-lead">{{ lead }}</p>
                <div class="mkt-detail-actions">
                  <a class="mkt-button mkt-button-accent" href="#detail-inquiry">
                    {{ t('marketing.detail.common.quote') }}<i class="mdi mdi-arrow-right" aria-hidden="true"></i>
                  </a>
                  <a class="mkt-button mkt-button-outline" :href="businessContact.whatsappHref"
                     target="_blank" rel="noopener">
                    <i class="mdi mdi-whatsapp" aria-hidden="true"></i>{{ t('marketing.detail.common.whatsapp') }}
                  </a>
                </div>
                <p class="mkt-detail-response"><i class="mdi mdi-account-check" aria-hidden="true"></i>{{ t('marketing.detail.common.response') }}</p>
              </div>
            </div>
          </div>
        </section>

        <section class="mkt-detail-trust-band">
          <div class="mkt-shell mkt-detail-trust-grid">
            <article v-for="item in t('marketing.detail.product.trust')" :key="item.title">
              <i class="mdi" :class="item.icon" aria-hidden="true"></i>
              <div><h2>{{ item.title }}</h2><p>{{ item.text }}</p></div>
            </article>
          </div>
        </section>

        <section class="mkt-section mkt-detail-content-section">
          <div class="mkt-shell">
            <div class="mkt-section-heading">
              <div>
                <span class="mkt-eyebrow">{{ t('marketing.detail.product.detailsEyebrow') }}</span>
                <h2>{{ t('marketing.detail.product.detailsTitle') }}</h2>
              </div>
              <p>{{ lead }}</p>
            </div>
            <div class="mkt-detail-reading-layout">
              <div class="mkt-detail-body">
                <section v-if="detail.Description" class="mkt-detail-rich-section">
                  <h2>{{ t('marketing.detail.product.descriptionTitle') }}</h2>
                  <div class="mkt-rich-content" v-html="detail.Description"></div>
                </section>
                <section v-if="detail.parameter" class="mkt-detail-rich-section">
                  <h2>{{ t('marketing.detail.product.parameterTitle') }}</h2>
                  <div class="mkt-rich-content mkt-parameter-content" v-html="detail.parameter"></div>
                </section>
              </div>
              <aside class="mkt-detail-side-cta">
                <span>{{ t('marketing.detail.product.inquiryEyebrow') }}</span>
                <h2>{{ t('marketing.detail.product.inquiryTitle') }}</h2>
                <p>{{ t('marketing.detail.product.inquiryText') }}</p>
                <a class="mkt-button mkt-button-primary" href="#detail-inquiry">{{ t('marketing.detail.common.quote') }}</a>
              </aside>
            </div>
          </div>
        </section>

        <section v-if="detail.video" class="mkt-detail-video-band">
          <div class="mkt-shell mkt-detail-video-layout">
            <div>
              <span class="mkt-eyebrow is-light">{{ t('marketing.detail.product.videoTitle') }}</span>
              <h2>{{ t('marketing.detail.product.videoTitle') }}</h2>
              <p>{{ t('marketing.detail.product.videoText') }}</p>
            </div>
            <div class="mkt-detail-video-frame">
              <iframe :src="detail.video" :title="detail.product_title" loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
            </div>
          </div>
        </section>

        <section v-if="relatedProducts.length" class="mkt-section mkt-detail-related-section">
          <div class="mkt-shell">
            <div class="mkt-detail-section-header">
              <div><span class="mkt-eyebrow">{{ t('marketing.detail.product.relatedEyebrow') }}</span><h2>{{ t('marketing.detail.product.relatedTitle') }}</h2></div>
              <a class="mkt-text-link" href="/products">{{ t('marketing.detail.common.viewAll') }}<i class="mdi mdi-arrow-right"></i></a>
            </div>
            <div class="mkt-detail-related-grid">
              <article v-for="item in relatedProducts" :key="item.id">
                <a :href="`/products/${encodeURIComponent(item.site || item.id)}`">
                  <img :src="item.image_1" :alt="item.product_title" loading="lazy" @error="applyImageFallback">
                  <div><span>{{ t('marketing.detail.product.eyebrow') }}</span><h3>{{ item.product_title }}</h3><i class="mdi mdi-arrow-top-right"></i></div>
                </a>
              </article>
            </div>
          </div>
        </section>

        <section id="detail-inquiry" class="mkt-inquiry-section mkt-detail-inquiry-section">
          <div class="mkt-shell mkt-inquiry-layout">
            <div class="mkt-inquiry-copy">
              <span class="mkt-eyebrow is-light">{{ t('marketing.detail.product.inquiryEyebrow') }}</span>
              <h2>{{ t('marketing.detail.product.inquiryTitle') }}</h2>
              <p>{{ t('marketing.detail.product.inquiryText') }}</p>
              <ul class="mkt-check-list is-light">
                <li v-for="item in t('marketing.home.inquiry.benefits')" :key="item"><i class="mdi mdi-check"></i>{{ item }}</li>
              </ul>
            </div>
            <MarketingInquiryForm :context="pageContext"/>
          </div>
        </section>
      </template>
    </main>
    <Footer/>
  </div>
</template>
